import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ChevronRight, LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from '@/components/common/Collapsible';
import { Button } from '@/components/common/Button';
import { cn } from '@/utils/utils';
import { WrapperSheetClose } from '../SheetMenu';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger
} from '@/components/common/DropdownMenu';
import { DropdownMenuArrow } from '@radix-ui/react-dropdown-menu';
import { isPathEk } from '@/utils/routes';
import { useStore } from '@/hooks/useStore';
import { ekDomain } from '@/configs/constants';

interface Submenu {
    href: string,
    label: string,
    active: boolean,
    submenus?: Submenu[]
}

interface CollapseMenuButtonProps {
    icon?: LucideIcon,
    label: string,
    active: boolean,
    submenus: Submenu[],
    isOpen?: boolean,
    useSheetClose?: boolean,
    isSubmenu?: boolean,
    level?: number
}

export default function CollapseMenuButton({
    icon: Icon,
    label,
    active,
    submenus,
    isOpen,
    useSheetClose,
    isSubmenu = false,
    level = 1
}: CollapseMenuButtonProps) {
    // hooks
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    // const
    const isEk = isPathEk(pathname);

    // store
    const {
        clearAllStore
    } = useStore();

    // state
    function isMenuActive(menu: Submenu): boolean {
        if (menu.submenus && menu.submenus.length > 0) {
            // Check in submenu
            return menu.submenus.some(isMenuActive);
        }
        // Check current menu
        return menu.active;
    }
    const isSubmenuActive = submenus.some(submenu => isMenuActive(submenu));

    const [isCollapsed, setIsCollapsed] = useState<boolean>(isSubmenuActive);

    const renderSubMenu = (menuList: Submenu[]) => {
        return menuList.map(({ href, label, active, submenus }, index) => {
            return submenus ?
                (
                    <CollapseMenuButton
                        key={index}
                        icon={Icon}
                        label={label}
                        active={active}
                        submenus={submenus}
                        isOpen={isOpen}
                        useSheetClose={useSheetClose}
                        isSubmenu={true}
                        level={level + 1}
                    />
                ) :
                (
                    isOpen ?
                        (
                            <div key={index} className='w-full'>
                                {/* <Tooltip
                                    content={t(label)}
                                    triggerAsChild={true}
                                    side='right'
                                > */}
                                <WrapperSheetClose useSheetClose={useSheetClose}>
                                    <Button
                                        variant={active ? 'activeMenu' : 'ghostMenu'}
                                        className={cn('justify-start h-10 mb-1', isSubmenu ? '' : 'w-full')}
                                        style={{ marginLeft: `calc((18px + 16px) * ${level})` }}
                                        asChild
                                        onClick={() => {
                                            !active && clearAllStore();
                                        }}
                                    >
                                        <Link to={isEk ? `/${ekDomain}${href}` : href}>
                                            <p className={cn('max-w-[170px] truncate', isOpen ? 'translate-x-0 opacity-100' : '-translate-x-96 opacity-0')}>
                                                {t(label)}
                                            </p>
                                        </Link>
                                    </Button>
                                </WrapperSheetClose>
                                {/* </Tooltip> */}
                            </div>
                        ) :
                        (
                            <DropdownMenuItem
                                key={index}
                                asChild
                                useFocusCss={false}
                                className='cursor-pointer'
                                onClick={() => navigate(isEk ? `/${ekDomain}${href}` : href)}
                            >
                                <Button
                                    variant={active ? 'activeMenu' : 'ghostMenu'}
                                    className='w-full justify-start h-10 mb-1'
                                    type='button'
                                >
                                    <p className='max-w-[180px] truncate'>{t(label)}</p>
                                </Button>
                            </DropdownMenuItem>
                        )
                );
        });
    };

    return isOpen ?
        (
            <Collapsible open={isCollapsed} onOpenChange={setIsCollapsed} className='w-full'>
                {/* <Tooltip
                    content={t(label)}
                    triggerAsChild={true}
                    side='right'
                    triggerClassName='w-full'
                > */}
                <CollapsibleTrigger
                    className='[&[data-state=open]>div>svg]:rotate-90 mb-1'
                    asChild
                >
                    <Button variant={active ? 'activeMenu' : 'ghostMenu'} className='w-full justify-start h-10'>
                        <div className='w-full flex items-center justify-between'>
                            <div className='flex items-center'>
                                {Icon && <Icon size={18} className={cn('mr-4', isSubmenu && 'opacity-0')} />}
                                <p className={cn('max-w-[150px] truncate', isOpen ? 'translate-x-0 opacity-100' : '-translate-x-96 opacity-0')}>
                                    {t(label)}
                                </p>
                            </div>
                            <ChevronRight
                                size={18}
                                className={cn(
                                    'transition-transform duration-300',
                                    isCollapsed ? 'rotate-90' : 'rotate-0'
                                )}
                            />
                        </div>
                    </Button>
                </CollapsibleTrigger>
                {/* </Tooltip> */}
                <CollapsibleContent className='overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down'>
                    {renderSubMenu(submenus)}
                </CollapsibleContent>
            </Collapsible>
        ) :
        (
            isSubmenu ?
                (
                    <DropdownMenuSub>
                        <DropdownMenuSubTrigger className='px-0 py-0'>
                            <Button variant={active ? 'activeMenu' : 'ghostMenu'} className='w-full justify-start h-10 mb-1'>
                                <div className={cn('w-full flex items-center', !isSubmenu && isOpen === false ? 'justify-center' : '')}>
                                    {Icon && !isSubmenu && <Icon size={18} className={cn(isOpen === false ? '' : 'mr-4')} />}
                                    <p className={cn('max-w-[200px] truncate', !isSubmenu && isOpen === false ? 'opacity-0 hidden' : 'opacity-100')}>
                                        {t(label)}
                                    </p>
                                </div>
                            </Button>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent
                            sideOffset={5}
                            className='bg-black-5 text-[#fff]'
                        >
                            <DropdownMenuLabel className='max-w-[190px] truncate'>{t(label)}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {renderSubMenu(submenus)}
                            <DropdownMenuArrow className='fill-border' />
                        </DropdownMenuSubContent>
                    </DropdownMenuSub>
                ) :
                (
                    <DropdownMenu>
                        {/* <Tooltip content={t(label)} triggerAsChild side='right' align='start' alignOffset={2}> */}
                        <DropdownMenuTrigger asChild>
                            <Button variant={active ? 'activeMenu' : 'ghostMenu'} className='w-full justify-start h-10 mb-1'>
                                <div className={cn('w-full flex items-center', !isSubmenu && isOpen === false ? 'justify-center' : '')}>
                                    {Icon && !isSubmenu && <Icon size={18} className={cn(isOpen === false ? '' : 'mr-4')} />}
                                    <p className={cn('max-w-[200px] truncate', !isSubmenu && isOpen === false ? 'opacity-0 hidden' : 'opacity-100')}>
                                        {t(label)}
                                    </p>
                                </div>
                            </Button>
                        </DropdownMenuTrigger>
                        {/* </Tooltip> */}
                        <DropdownMenuContent
                            side='right'
                            sideOffset={25}
                            align='start'
                            className='bg-black-5 text-[#fff]'
                        >
                            <DropdownMenuLabel className='max-w-[190px] truncate'>{t(label)}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {renderSubMenu(submenus)}
                            <DropdownMenuArrow className='fill-border' />
                        </DropdownMenuContent>
                    </DropdownMenu>
                )
        );
}
